/**
 * Event landing-page template — 05-dinner ("Soirée — Menu Dégustation").
 *
 * Distinct CraftJS home-page document for the `dinner` theme. Cloned from the
 * proven `webinar` template structure; content/copy authored from the design
 * spec in TEMPLATES - LANDINPAGES/05-dinner/sections/*.html (CRAFT_PROPS_HINT
 * blocks). German copy lifted faithfully; {{placeholders}} replaced with
 * concrete static copy (next-intl's ICU parser throws on `{{…}}`).
 *
 * Visual style: editorial-menu — warm cream/ivory background, deep
 * burgundy/wine accent, editorial serif (menu-card feel), soft rounding.
 *
 * Convention (mirrors webinar.ts):
 *  - Section-level marketing copy goes through td("din…") keys.
 *  - Structured child data (agenda rows, FAQ, numbers, testimonials,
 *    speaker roles, blog cards) also go through td("din…") keys; only
 *    proper names (people, wines, cheeses) stay literal.
 *  - Global colors/fonts come from DINNER_PRESET (applied via the theme).
 */
import { defaultRegistrationConsents, toCheckboxItems } from "@/lib/website/registrationConsents";
import {
  A,
  enc,
  pv,
  tc,
  btn,
  fi,
  acceptanceFi,
  scaffold,
  emptyFieldsGroup,
  PAD,
  DIST_CARD,
  BTN_FORM_SP,
  SEC_MARGIN,
  ACCEPTANCE_VISIBILITY_FILTER,
  type N,
  type TranslationFn,
} from "../temp";
import { EASE, decorLayer, fadeUpKeyframes, fontFaces, heroShell, motionGate, revealBlock } from "./premium-css";
import type { WebsiteThemeStylePreset } from "@/types/WebsiteTheme";
import {
  SAMPLE_SPEAKER_IMAGES,
  SAMPLE_SPONSOR_LOGOS,
} from "@/lib/website-builder/sampleContent";

// Dinner-specific asset namespace (seeded under <CDN>/dinner/…)
const W = `${A}/shared/templates/05-dinner`;

const ACCENT = "#7b1e2b"; // deep burgundy / wine
const INK = "#2a2419"; // near-black warm headings
const MUTE = "#6b6353"; // muted warm body
const DARK = "#1f1a12"; // dark espresso section surface
const ON_DARK = "#e8dcbf"; // body text on dark
const ON_DARK_ACCENT = "#c9a96e"; // brass eyebrow on dark
const SERIF = "'Playfair Display', Georgia, 'Times New Roman', serif";

const LOGO = `${A}/shared/logo.svg`;
// Speaker/testimonial faces come from the shared sample rotation
// (lib/website-builder/sampleContent); the template's own `<family>/*`
// art stays what it is: heroes, details and gallery photography.

// ── Localized child data (td-keyed; wine/proper names stay literal) ───

const NUMBERS_DATA = (td: TranslationFn) => [
  { num: "<b>VI</b>", desc: td("dinNum1Desc") },
  { num: "<b>12</b>", desc: td("dinNum2Desc") },
  { num: "<b>VIII</b>", desc: td("dinNum3Desc") },
  { num: "<b>2 ★</b>", desc: td("dinNum4Desc") },
];

const AGENDA_DATA = (td: TranslationFn) => [
  { title: td("dinAgendaRow1Title"), speaker: "Champagne Egly-Ouriet Grand Cru", desc: td("dinAgendaRow1Desc") },
  { title: td("dinAgendaRow2Title"), speaker: "Puligny-Montrachet 1er Cru · Sauzet 2021", desc: td("dinAgendaRow2Desc") },
  { title: td("dinAgendaRow3Title"), speaker: "Gevrey-Chambertin · Domaine Fourrier 2019", desc: td("dinAgendaRow3Desc") },
  { title: td("dinAgendaRow4Title"), speaker: "Vin Jaune · Château-Chalon 2014", desc: td("dinAgendaRow4Desc") },
  { title: td("dinAgendaRow5Title"), speaker: "Sauternes · Château d'Yquem 2015", desc: td("dinAgendaRow5Desc") },
  { title: td("dinAgendaRow6Title"), speaker: "Castarède Armagnac 1986", desc: td("dinAgendaRow6Desc") },
];

const SPEAKER_DATA = (td: TranslationFn) => [
  { name: "Antoine Mercier", role: td("dinSpeaker1Role"), img: "01" },
  { name: "Élise Laurent", role: td("dinSpeaker2Role"), img: "02" },
  { name: "Sophie Lambert", role: td("dinSpeaker3Role"), img: "03" },
  { name: "Marc Beaumont", role: td("dinSpeaker4Role"), img: "04" },
  { name: "Inès Toussaint", role: td("dinSpeaker5Role"), img: "05" },
  { name: "Tomás Ferrer", role: td("dinSpeaker6Role"), img: "06" },
];

const TESTIMONIAL_DATA = (td: TranslationFn) => [
  { quote: td("dinTestimonial1Quote"), name: "Dr. Caroline Berger", role: td("dinTestimonial1Role") },
  { quote: td("dinTestimonial2Quote"), name: "Markus Heller", role: td("dinTestimonial2Role") },
  { quote: td("dinTestimonial3Quote"), name: "Yara Costa", role: td("dinTestimonial3Role") },
];

const BLOG_DATA = (td: TranslationFn) => [
  { topic: `<b>${td("dinBlog1Topic")}</b>`, content: td("dinBlog1Content") },
  { topic: `<b>${td("dinBlog2Topic")}</b>`, content: td("dinBlog2Content") },
  { topic: `<b>${td("dinBlog3Topic")}</b>`, content: td("dinBlog3Content") },
];

const FAQ_DATA = (td: TranslationFn) => [
  { q: `<b>${td("dinFaq1Question")}</b>`, a: td("dinFaq1Answer") },
  { q: `<b>${td("dinFaq2Question")}</b>`, a: td("dinFaq2Answer") },
  { q: `<b>${td("dinFaq3Question")}</b>`, a: td("dinFaq3Answer") },
  { q: `<b>${td("dinFaq4Question")}</b>`, a: td("dinFaq4Answer") },
  { q: `<b>${td("dinFaq5Question")}</b>`, a: td("dinFaq5Answer") },
  { q: `<b>${td("dinFaq6Question")}</b>`, a: td("dinFaq6Answer") },
];

// ── Dinner-specific child factories ───────────────────────────────────

function numbersChild(parent: string, i: number, td: TranslationFn): N {
  const d = NUMBERS_DATA(td)[i];
  return {
    type: { resolvedName: "NumbersChild" }, isCanvas: false,
    props: {
      isVisible: true,
      isVisibleNumber: true, textNumber: d.num, fontSizeNumber: "text-4xl", textAlignNumber: "center",
      isVisibleDescription: true, textDescription: d.desc, fontSizeDescription: "text-sm", textAlignDescription: "center",
    },
    displayName: "NumbersChild", custom: { icon: "IconNumber" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function agendaChild(parent: string, i: number, td: TranslationFn): N {
  const d = AGENDA_DATA(td)[i];
  return {
    type: { resolvedName: "AgendaChild" }, isCanvas: false,
    props: {
      isVisibleText1: true, textText1: `<b>${td("dinAgendaTime")}</b>`, fontSizeText1: "text-sm", textAlignText1: "left",
      isVisibleText2: true, textText2: d.title, fontSizeText2: "text-sm", textAlignText2: "left",
      isVisibleText3: true, textText3: d.speaker, fontSizeText3: "text-sm", textAlignText3: "left",
      isVisibleText: true, textText: d.desc, fontSizeText: "text-sm", textAlignText: "left", border: true,
    },
    displayName: "AgendaChild", custom: { icon: "IconCalendar" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function speakerChild(parent: string, i: number, td: TranslationFn): N {
  const d = SPEAKER_DATA(td)[i];
  return {
    type: { resolvedName: "SpeakerChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length], imageBottomBackground: "transparent",
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left",
      icons: [{ icon: "IconBrandInstagram" }, { icon: "IconMail" }], isVisibleIcon: true,
    },
    displayName: "SpeakerChild", custom: { icon: "IconMicrophone" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function testimonialChild(parent: string, i: number, td: TranslationFn): N {
  const d = TESTIMONIAL_DATA(td)[i];
  return {
    type: { resolvedName: "TestimonialsChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPONSOR_LOGOS[i % SAMPLE_SPONSOR_LOGOS.length].src,
      imageUrlBottom: SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length],
      isVisibleImageTop: false, imageBottomBackground: "transparent", isVisibleImageBottom: false,
      isVisibleContent: true, textContent: d.quote, fontSizeContent: "text-md", textAlignContent: "left", fontColorContent: ON_DARK,
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left", fontColorName: "#fffcf7",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left", fontColorOccupation: ON_DARK,
    },
    displayName: "TestimonialsChild", custom: { icon: "IconThumbUp" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function blogChild(parent: string, i: number, td: TranslationFn): N {
  const d = BLOG_DATA(td)[i];
  return {
    type: { resolvedName: "BlogChild" }, isCanvas: false,
    props: {
      iconAlignment: "left", isVisible: true,
      isVisibleTopic: true, textTopic: d.topic, fontSizeTopic: "text-lg", textAlignTopic: "left",
      isVisibleContent: true, textContent: d.content, fontSizeContent: "text-md", textAlignContent: "left",
      isVisibleButton: false, size: "small", text: td("learnMore"), underline: false, border: true,
    },
    displayName: "BlogChild", custom: { icon: "IconArticle" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function faqChild(parent: string, i: number, td: TranslationFn): N {
  const d = FAQ_DATA(td)[i];
  return {
    type: { resolvedName: "FaQChild" }, isCanvas: false,
    props: {
      isVisibleQuestion: true, textQuestion: d.q, fontSizeQuestion: "text-sm", textAlignQuestion: "left",
      isVisibleAnswer: true, textAnswer: d.a, fontSizeAnswer: "text-sm", textAlignAnswer: "left", border: true,
    },
    displayName: "FaQChild", custom: { icon: "IconMessageQuestion" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

const GALLERY_IMG = [
  `${W}/hero-einladung.jpg`, `${W}/detail-menu-card.jpg`, `${W}/detail-wine.jpg`,
  `${W}/detail-wine-pour.jpg`, `${W}/hero-zusage.jpg`, `${W}/hero-save-the-date.jpg`,
];

function galleryImage(parent: string, i: number): N {
  return {
    type: { resolvedName: "ImageChild" }, isCanvas: false,
    props: { src: GALLERY_IMG[i % GALLERY_IMG.length], isVisible: true, aspectRatio: "aspect-video", displayName: "ImageChild", width: "w-full", canDuplicate: true },
    displayName: "ImageChild", custom: { icon: "IconSection" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

// ── Template ──────────────────────────────────────────────────────────

export const dinner = (td: TranslationFn): string => {
  const B = "din_body";

  const HERO = "din_hero";
  const NUMBERS = "din_numbers";
  const AGENDA = "din_agenda";
  const SPEAKER = "din_speaker";
  const TEXT = "din_text";
  const IMAGE = "din_image";
  const COUNTDOWN = "din_countdown";
  const GALLERY = "din_gallery";
  const VIDEO = "din_video";
  const TESTI = "din_testimonials";
  const BLOG = "din_blog";
  const FAQ = "din_faq";
  const MAP = "din_map";
  const DI = "din_datainput";
  const CONTACT = "din_contact";

  return enc({
    ...scaffold(
      { ab: "din_ab", abs: "din_abs", body: B, ft: "din_ft", fts: "din_fts" },
      [HERO, NUMBERS, DI, AGENDA, SPEAKER, TEXT, IMAGE, COUNTDOWN, GALLERY, VIDEO, TESTI, BLOG, FAQ, MAP, CONTACT],
      false, { background: "bg-white", padding: "p-4", borderRadius: "rounded-lg" },
    ),

    // ── Hero (image right, dark text on cream) ──
    [HERO]: {
      type: { resolvedName: "Hero" }, isCanvas: false,
      props: {
        sectionHeight: 88, contentAlignment: "center", imageLayout: "right", layoutWidth: "50%", inversed: true,
        padding: PAD, distButtons: "gap-3 wb-md:gap-4", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", buttonAlignment: "flex-row", isVisible: true,
        isVisibleHeadline: true, textHeadline: td("dinHeroEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left", headlineColor: ACCENT,
        isVisibleHeader: true, textHeader: td("dinHeroHeader"), fontSizeHeader: "text-6xl", textAlignHeader: "left", headerColor: INK,
        isVisibleText: true, textText: td("dinHeroText"), fontSizeText: "text-lg", textAlignText: "left", textColor: MUTE,
        secondaryButtonSize: "medium", secondaryButtonText: td("dinHeroSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("dinHeroPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
        backgroundType: "image", imageRounded: false, backgroundOverlayColor: "transparent",
        imageSrc: `${W}/hero-einladung.jpg`, backgroundColor: "#fffcf7",
      },
      displayName: "Hero", custom: { icon: "IconSlideshow", preview: pv("Hero-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "din_hero_eb", header: "din_hero_hd", text: "din_hero_tx", buttonOne: "din_hero_b1", buttonTwo: "din_hero_b2" },
    },
    din_hero_eb: tc(HERO, { isVisible: true, text: td("dinHeroEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    din_hero_hd: tc(HERO, { isVisible: true, text: td("dinHeroHeader"), fontSize: "text-6xl", textAlign: "left", displayName: "header", color: INK, header: true }),
    din_hero_tx: tc(HERO, { isVisible: true, text: td("dinHeroText"), fontSize: "text-lg", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    din_hero_b1: btn(HERO, { type: "secondary", text: td("dinHeroSecondaryButton"), displayName: "buttonOne" }),
    din_hero_b2: btn(HERO, { type: "primary", linkType: "internal", linkUrl: "#" + DI, text: td("dinHeroPrimaryButton"), displayName: "buttonTwo" }),

    // ── Numbers ──
    [NUMBERS]: {
      type: { resolvedName: "Numbers" }, isCanvas: true,
      props: {
        sectionHeight: 60, contentAlignment: "center", sectionMargin: SEC_MARGIN, gridColumns: "4",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("dinNumbersHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("dinNumbersHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("dinNumbersText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Numbers", custom: { icon: "Icon123", preview: pv("NumbersPreview"), allowedComponents: ["NumbersChild"] },
      parent: B, hidden: false, nodes: ["din_num1", "din_num2", "din_num3", "din_num4"],
      linkedNodes: { headline: "din_num_eb", header: "din_num_hd", text: "din_num_tx" },
    },
    din_num_eb: tc(NUMBERS, { isVisible: false, text: td("dinNumbersHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    din_num_hd: tc(NUMBERS, { isVisible: true, text: td("dinNumbersHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    din_num_tx: tc(NUMBERS, { isVisible: true, text: td("dinNumbersText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    din_num1: numbersChild(NUMBERS, 0, td),
    din_num2: numbersChild(NUMBERS, 1, td),
    din_num3: numbersChild(NUMBERS, 2, td),
    din_num4: numbersChild(NUMBERS, 3, td),

    // ── Agenda (the menu / carte) ──
    [AGENDA]: {
      type: { resolvedName: "Agenda" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("dinAgendaHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("dinAgendaHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("dinAgendaText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Agenda", custom: { icon: "IconListCheck", preview: pv("AgendaPreview"), allowedComponents: ["AgendaChild"] },
      parent: B, hidden: false, nodes: ["din_ag1", "din_ag2", "din_ag3", "din_ag4", "din_ag5", "din_ag6"],
      linkedNodes: { headline: "din_ag_eb", header: "din_ag_hd", text: "din_ag_tx" },
    },
    din_ag_eb: tc(AGENDA, { isVisible: false, text: td("dinAgendaHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    din_ag_hd: tc(AGENDA, { isVisible: true, text: td("dinAgendaHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    din_ag_tx: tc(AGENDA, { isVisible: true, text: td("dinAgendaText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    din_ag1: agendaChild(AGENDA, 0, td),
    din_ag2: agendaChild(AGENDA, 1, td),
    din_ag3: agendaChild(AGENDA, 2, td),
    din_ag4: agendaChild(AGENDA, 3, td),
    din_ag5: agendaChild(AGENDA, 4, td),
    din_ag6: agendaChild(AGENDA, 5, td),

    // ── Speaker (la brigade) — hidden by default (no portraits) ──
    [SPEAKER]: {
      type: { resolvedName: "Speaker" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextSpeakers: "gap-8 wb-md:gap-12", distSpeakers: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, gridColumns: "3", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("dinSpeakerHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("dinSpeakerHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("dinSpeakerText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Speaker", custom: { icon: "IconUserStar", preview: pv("Speaker-preview"), allowedComponents: ["SpeakerChild"] },
      parent: B, hidden: false, nodes: ["din_sp1", "din_sp2", "din_sp3", "din_sp4", "din_sp5", "din_sp6"],
      linkedNodes: { headline: "din_sp_eb", header: "din_sp_hd", text: "din_sp_tx" },
    },
    din_sp_eb: tc(SPEAKER, { isVisible: false, text: td("dinSpeakerHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    din_sp_hd: tc(SPEAKER, { isVisible: true, text: td("dinSpeakerHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    din_sp_tx: tc(SPEAKER, { isVisible: true, text: td("dinSpeakerText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    din_sp1: speakerChild(SPEAKER, 0, td),
    din_sp2: speakerChild(SPEAKER, 1, td),
    din_sp3: speakerChild(SPEAKER, 2, td),
    din_sp4: speakerChild(SPEAKER, 3, td),
    din_sp5: speakerChild(SPEAKER, 4, td),
    din_sp6: speakerChild(SPEAKER, 5, td),

    // ── Text (note from the chef) ──
    [TEXT]: {
      type: { resolvedName: "TextSection" }, isCanvas: false,
      props: {
        buttonAlign: "center", padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("dinTextHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("dinTextHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("dinTextBody"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("dinTextSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false, secondaryButtonAlign: "center",
        primaryButtonSize: "medium", primaryButtonText: td("dinTextPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false, primaryButtonAlign: "center",
      },
      displayName: "Text", custom: { icon: "IconAlignJustified", preview: pv("textPreview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "din_tx_eb", header: "din_tx_hd", text: "din_tx_tx", buttonOne: "din_tx_b1", buttonTwo: "din_tx_b2" },
    },
    din_tx_eb: tc(TEXT, { isVisible: false, text: td("dinTextHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    din_tx_hd: tc(TEXT, { isVisible: true, text: td("dinTextHeader"), fontSize: "text-4xl", textAlign: "center", displayName: "header", header: true }),
    din_tx_tx: tc(TEXT, { isVisible: true, text: td("dinTextBody"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    din_tx_b1: btn(TEXT, { type: "secondary", text: td("dinTextSecondaryButton"), displayName: "buttonOne" }),
    din_tx_b2: btn(TEXT, { type: "primary", text: td("dinTextPrimaryButton"), displayName: "buttonTwo" }),

    // ── Image (Acte III · le plat — image left / text right) ──
    [IMAGE]: {
      type: { resolvedName: "Image" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, inversed: true,
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true,
        isVisibleHeadline: false, textHeadline: td("dinImageHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("dinImageHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("dinImageText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("dinImageButton"), isVisibleButton: true, buttonAlign: "left",
        buttonBorder: true, buttonUnderline: false,
        imageRounded: "rounded-lg", layoutWidth: "50%",
        isVisibleImage: true, imageSrc: `${W}/detail-menu-card.jpg`, imageAspectRatio: "aspect-[4/5]",
      },
      displayName: "Image", custom: { icon: "IconSection", preview: pv("Image-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "din_im_eb", header: "din_im_hd", text: "din_im_tx", button: "din_im_btn", image: "din_im_img" },
    },
    din_im_eb: tc(IMAGE, { isVisible: false, text: td("dinImageHeader"), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true }),
    din_im_hd: tc(IMAGE, { isVisible: true, text: td("dinImageHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    din_im_tx: tc(IMAGE, { isVisible: true, text: td("dinImageText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    din_im_btn: btn(IMAGE, { text: td("dinImageButton"), displayName: "button" }),
    din_im_img: {
      type: { resolvedName: "ImageChild" }, isCanvas: false,
      props: { src: `${W}/detail-menu-card.jpg`, isVisible: true, aspectRatio: "aspect-[4/5]", displayName: "ImageChild", width: "w-full wb-lg:w-1/2", canDuplicate: false },
      displayName: "ImageChild", custom: { icon: "IconSection" },
      parent: IMAGE, hidden: false, nodes: [], linkedNodes: {},
    },

    // ── Countdown ──
    [COUNTDOWN]: {
      type: { resolvedName: "Countdown" }, isCanvas: false,
      props: {
        sectionHeight: 40, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distButtonCountdown: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true, buttonAlign: "left",
        isVisibleHeadline: true, textHeadline: td("dinCountdownEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("dinCountdownHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("dinCountdownText"), fontSizeText: "text-base", textAlignText: "left",
        buttonSize: "medium", buttonText: td("dinCountdownButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
        isVisiblCountdown: true, countdownSizeDate: "text-5xl", countdownFontTypeDate: SERIF, countdownFontColorDate: INK,
        countdownSize: "text-base", countdownFontColor: "#8b6f3a", countdownBorderColor: "#e8dcbf",
      },
      displayName: "Countdown", custom: { icon: "IconAlarm", preview: pv("Countdown-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "din_cd_eb", header: "din_cd_hd", text: "din_cd_tx", button: "din_cd_btn" },
    },
    din_cd_eb: tc(COUNTDOWN, { isVisible: true, text: td("dinCountdownEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    din_cd_hd: tc(COUNTDOWN, { isVisible: true, text: td("dinCountdownHeader"), fontSize: "text-4xl", textAlign: "left", displayName: "header", color: INK, header: true }),
    din_cd_tx: tc(COUNTDOWN, { isVisible: true, text: td("dinCountdownText"), fontSize: "text-base", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    din_cd_btn: btn(COUNTDOWN, { text: td("dinCountdownButton"), displayName: "ButtonChild" }),

    // ── Gallery (tableau) — hidden by default ──
    [GALLERY]: {
      type: { resolvedName: "Gallery" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", distButtonGallery: "gap-3 wb-md:gap-4", distEntries: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, buttonAlign: "center", gridColumns: "4", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("dinGalleryHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("dinGalleryHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: false, textText: td("dinGalleryHeader"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("dinGallerySecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("dinGalleryPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
      },
      displayName: "Gallery", custom: { icon: "IconPhoto", preview: pv("Gallery-preview"), allowedComponents: ["ImageChild"] },
      parent: B, hidden: false,
      nodes: ["din_g1", "din_g2", "din_g3", "din_g4", "din_g5", "din_g6"],
      linkedNodes: { headline: "din_g_eb", header: "din_g_hd", text: "din_g_tx", secondary: "din_g_b2", primary: "din_g_b1" },
    },
    din_g_eb: tc(GALLERY, { isVisible: false, text: td("dinGalleryHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    din_g_hd: tc(GALLERY, { isVisible: true, text: td("dinGalleryHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    din_g_tx: tc(GALLERY, { isVisible: false, text: td("dinGalleryHeader"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    din_g_b2: btn(GALLERY, { type: "secondary", text: td("dinGallerySecondaryButton"), displayName: "ButtonChild" }),
    din_g_b1: btn(GALLERY, { text: td("dinGalleryPrimaryButton"), displayName: "ButtonChild" }),
    din_g1: galleryImage(GALLERY, 0),
    din_g2: galleryImage(GALLERY, 1),
    din_g3: galleryImage(GALLERY, 2),
    din_g4: galleryImage(GALLERY, 3),
    din_g5: galleryImage(GALLERY, 4),
    din_g6: galleryImage(GALLERY, 5),

    // ── Video (behind the pass) — hidden by default ──
    [VIDEO]: {
      type: { resolvedName: "Video" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentVideo: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false,
        isVisibleHeadline: false, textHeadline: td("dinVideoHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("dinVideoHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("dinVideoText"), fontSizeText: "text-md", textAlignText: "center",
        buttonSize: "medium", buttonText: td("dinVideoButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "center",
        videoAspectRatio: "aspect-video", isVisibleVideo: true, videoUrl: "https://youtu.be/sdESKm-nKQE",
      },
      displayName: "Video", custom: { icon: "IconVideo", preview: pv("Video-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "din_vd_eb", header: "din_vd_hd", text: "din_vd_tx", button: "din_vd_btn" },
    },
    din_vd_eb: tc(VIDEO, { isVisible: false, text: td("dinVideoHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    din_vd_hd: tc(VIDEO, { isVisible: true, text: td("dinVideoHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    din_vd_tx: tc(VIDEO, { isVisible: true, text: td("dinVideoText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    din_vd_btn: btn(VIDEO, { text: td("dinVideoButton"), isVisible: true, displayName: "button" }),

    // ── Testimonials (dark espresso surface) ──
    [TESTI]: {
      type: { resolvedName: "Testimonials" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextTestimonials: "gap-8 wb-md:gap-12", distTestimonials: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: true, gridColumns: "3", layoutWidth: "100%",
        backgroundColor: DARK,
        isVisibleHeadline: true, textHeadline: td("dinTestimonialsEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("dinTestimonialsHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: false, textText: td("dinTestimonialsHeader"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Testimonials", custom: { icon: "IconQuote", preview: pv("Testimonials-preview"), allowedComponents: ["TestimonialsChild"] },
      parent: B, hidden: false, nodes: ["din_ts1", "din_ts2", "din_ts3"],
      linkedNodes: { headline: "din_ts_eb", header: "din_ts_hd", text: "din_ts_tx" },
    },
    din_ts_eb: tc(TESTI, { isVisible: true, text: td("dinTestimonialsEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ON_DARK_ACCENT, header: true }),
    din_ts_hd: tc(TESTI, { isVisible: true, text: td("dinTestimonialsHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: "#fffcf7", header: true }),
    din_ts_tx: tc(TESTI, { isVisible: false, text: td("dinTestimonialsHeader"), fontSize: "text-md", textAlign: "center", displayName: "text", color: ON_DARK, header: false }),
    din_ts1: testimonialChild(TESTI, 0, td),
    din_ts2: testimonialChild(TESTI, 1, td),
    din_ts3: testimonialChild(TESTI, 2, td),

    // ── Blog (le carnet) — hidden by default ──
    [BLOG]: {
      type: { resolvedName: "Blog" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, gridColumns: "3", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("dinBlogHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("dinBlogHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("dinBlogText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Blog", custom: { icon: "IconClick", preview: pv("Blog-preview"), allowedComponents: ["BlogChild"] },
      parent: B, hidden: false, nodes: ["din_bl1", "din_bl2", "din_bl3"],
      linkedNodes: { headline: "din_bl_eb", header: "din_bl_hd", text: "din_bl_tx" },
    },
    din_bl_eb: tc(BLOG, { isVisible: false, text: td("dinBlogHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    din_bl_hd: tc(BLOG, { isVisible: true, text: td("dinBlogHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    din_bl_tx: tc(BLOG, { isVisible: true, text: td("dinBlogText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    din_bl1: blogChild(BLOG, 0, td),
    din_bl2: blogChild(BLOG, 1, td),
    din_bl3: blogChild(BLOG, 2, td),

    // ── FaQ ──
    [FAQ]: {
      type: { resolvedName: "FaQ" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("dinFaqHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("dinFaqHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("dinFaqText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "FaQ", custom: { icon: "IconQuestionMark", preview: pv("faqPreview"), allowedComponents: ["FaQChild"] },
      parent: B, hidden: false, nodes: ["din_fq1", "din_fq2", "din_fq3", "din_fq4", "din_fq5", "din_fq6"],
      linkedNodes: { headline: "din_fq_eb", header: "din_fq_hd", text: "din_fq_tx" },
    },
    din_fq_eb: tc(FAQ, { isVisible: false, text: td("dinFaqHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    din_fq_hd: tc(FAQ, { isVisible: true, text: td("dinFaqHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    din_fq_tx: tc(FAQ, { isVisible: true, text: td("dinFaqText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    din_fq1: faqChild(FAQ, 0, td),
    din_fq2: faqChild(FAQ, 1, td),
    din_fq3: faqChild(FAQ, 2, td),
    din_fq4: faqChild(FAQ, 3, td),
    din_fq5: faqChild(FAQ, 4, td),
    din_fq6: faqChild(FAQ, 5, td),

    // ── Map (the maison) — hidden by default ──
    [MAP]: {
      type: { resolvedName: "Map" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentMap: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false,
        isVisibleHeadline: false, textHeadline: td("dinMapHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("dinMapHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("dinMapText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("dinMapButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "left",
        mapRounded: "rounded-lg",
        mapZoom: 14, mapType: "m", aspectRatio: "aspect-[4/3]", isVisibleMap: true, mapLocation: td("dinMapLocation"),
      },
      displayName: "Map", custom: { icon: "IconMap2", preview: pv("Map-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "din_mp_eb", header: "din_mp_hd", text: "din_mp_tx", button: "din_mp_btn" },
    },
    din_mp_eb: tc(MAP, { isVisible: false, text: td("dinMapHeader"), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true }),
    din_mp_hd: tc(MAP, { isVisible: true, text: td("dinMapHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    din_mp_tx: tc(MAP, { isVisible: true, text: td("dinMapText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    din_mp_btn: btn(MAP, { text: td("dinMapButton"), displayName: "button" }),

    // ── DataInput (multi-step RSVP) ──
    [DI]: {
      type: { resolvedName: "DataInput" }, isCanvas: false,
      props: {
        padding: PAD, sectionMargin: SEC_MARGIN, isVisible: true, textSpacing: DIST_CARD,
        backgroundColor: "transparent", border: false, borderWidth: "border-2", borderStyle: "border-solid",
        borderSides: ["bottom"], borderRadius: undefined,
        isVisibleHeadline: true, textHeadline: td("dinDataHeadline"), fontSizeHeadline: "text-base", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("dinDataHeader"), fontSizeHeader: "text-lg", textAlignHeader: "center",
        isVisibleText: true, textText: td("dinDataText"), fontSizeText: "text-sm", textAlignText: "center",
        allowUpdate: true,
        multiStepEnabled: true, stepLabels: [td("dinStepConfirmed"), td("dinStepDiet"), td("dinStepPairing"), td("dinStepOverview")], multiStepMode: "pages",
        progressBarEnabled: true, progressBarColor: ACCENT,
        overviewEnabled: true, stepSections: [["fields-0"], ["fields-1"], ["fields-2"], []],
      },
      displayName: "DataInput", custom: { icon: "IconForms", preview: pv("DataInput-Preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "din_di_hl", header: "din_di_hd", text: "din_di_tx",
        fieldsGroup0: "din_di_f0", fieldsGroup1: "din_di_f1", fieldsGroup2: "din_di_f2",
        fieldsGroup3: "din_di_f3", fieldsGroup4: "din_di_f4",
        companionsGroup: "din_di_comp", overviewGroup: "din_di_ov", checkboxes: "din_di_cb",
        button: "din_di_btn", backButton: "din_di_back", nextButton: "din_di_next",
      },
    },
    din_di_hl: tc(DI, { isVisible: true, text: td("dinDataHeadline"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "center", displayName: "headline", header: true }),
    din_di_hd: tc(DI, { isVisible: true, text: td("dinDataHeader"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-lg", textAlign: "center", displayName: "header", header: true }),
    din_di_tx: tc(DI, { isVisible: true, text: td("dinDataText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "center", displayName: "text", header: false }),
    din_di_f0: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 4, groupIndex: 0, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["din_di_acceptance", "din_di_first", "din_di_last", "din_di_email"], linkedNodes: {},
    },
    // FIRST in the group, and filter-free — the question every other
    // field in this group is gated on. See `acceptanceFi`.
    din_di_acceptance: acceptanceFi("din_di_f0", td("acceptanceState")),
    din_di_first: fi("din_di_f0", { label: td("firstName"), requiredField: true, inputFieldVariable: "firstName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    din_di_last: fi("din_di_f0", { label: td("lastName"), requiredField: true, inputFieldVariable: "lastName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    din_di_email: fi("din_di_f0", { label: td("email"), requiredField: true, inputFieldVariable: "email", gridArea: "col-span-12", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    din_di_f1: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 2, groupIndex: 1, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["din_di_diet", "din_di_allergies"], linkedNodes: {},
    },
    din_di_diet: fi("din_di_f1", { label: td("dinFieldDiet"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "diet", changeable: true }),
    din_di_allergies: fi("din_di_f1", { label: td("dinFieldAllergies"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "allergies", changeable: true }),
    din_di_f2: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 1, groupIndex: 2, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["din_di_pairing"], linkedNodes: {},
    },
    din_di_pairing: fi("din_di_f2", { label: td("dinFieldPairing"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "pairing", multiline: true, gridArea: "col-span-12", changeable: true }),
    din_di_f3: emptyFieldsGroup(DI, 3),
    din_di_f4: emptyFieldsGroup(DI, 4),
    din_di_comp: {
      type: { resolvedName: "CompanionsGroup" }, isCanvas: true,
      props: { isVisible: false, fieldCount: 1, fieldSpacing: "medium", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER },
      displayName: "CompanionsGroup", custom: { icon: "IconUsers", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["din_di_comp_first"], linkedNodes: {},
    },
    din_di_comp_first: fi("din_di_comp", { label: td("firstName"), requiredField: false, inputFieldVariable: "firstName", gridArea: "col-span-12", changeable: true, type: "companion" }),
    din_di_ov_t: tc("din_di_ov", { isVisible: true, text: td("overviewTitle"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "left", displayName: "overviewTitle", header: true }),
    din_di_ov_x: tc("din_di_ov", { isVisible: true, text: td("overviewText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "left", displayName: "overviewText", header: false }),
    din_di_ov: {
      type: { resolvedName: "OverviewGroup" }, isCanvas: true,
      props: { isVisible: false, parentNodeId: DI, stepSections: [["fields-0"], ["fields-1"], ["fields-2"], []], stepLabels: [] },
      displayName: "summaryGroup", custom: { icon: "IconListDetails" },
      parent: DI, hidden: false, nodes: ["din_di_ov_t", "din_di_ov_x"], linkedNodes: {},
    },
    din_di_cb: {
      type: { resolvedName: "Checkboxes" }, isCanvas: false,
      props: { isVisible: true, checkboxes: toCheckboxItems(defaultRegistrationConsents(td)), displayName: "checkboxes" },
      displayName: "Checkboxes", custom: { icon: "IconCheckbox" },
      parent: DI, hidden: false, nodes: [], linkedNodes: {},
    },
    din_di_btn: btn(DI, { linkType: "internal", linkUrl: "", text: td("dinDataSubmit"), submittedText: "", isSubmitted: false, enableSubmittedState: true, size: "small", widthMode: "full", buttonType: "submit", margin: BTN_FORM_SP, displayName: "button" }),
    din_di_back: btn(DI, { linkType: "internal", type: "secondary", linkUrl: "", text: td("back"), navigationRole: "back", size: "small", widthMode: "content", isVisible: false, displayName: "backButton" }),
    din_di_next: btn(DI, { linkType: "internal", linkUrl: "", text: td("next"), navigationRole: "next", size: "small", widthMode: "content", isVisible: false, displayName: "nextButton" }),

    // ── Contact ──
    [CONTACT]: {
      type: { resolvedName: "Contact" }, isCanvas: true,
      props: {
        sectionHeight: 70, contentAlignment: "center", infoSection: true, backgroundColor: "transparent",
        padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-3 wb-md:space-y-5 wb-lg:space-y-6", isVisible: true,
        infoWidth: "50%", inversed: false,
        isVisibleHeadline: true, textHeadline: td("dinContactEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("dinContactHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("dinContactText"), fontSizeText: "text-md", textAlignText: "left",
        isSocialVisible: true, socialIcons: [{ platform: "instagram", url: "https://instagram.com" }, { platform: "facebook", url: "https://facebook.com" }], socialSize: 24, socialPosition: "left",
        buttonSize: "medium", buttonText: td("dinContactButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
      },
      displayName: "Contact", custom: { icon: "IconForms", preview: pv("Contact-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "din_ct_eb", header: "din_ct_hd", text: "din_ct_tx",
        firstName: "din_ct_first", lastName: "din_ct_last", eMail: "din_ct_email",
        phoneNumber: "din_ct_phone", Message: "din_ct_msg", button: "din_ct_btn",
      },
    },
    din_ct_eb: tc(CONTACT, { isVisible: true, text: td("dinContactEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    din_ct_hd: tc(CONTACT, { isVisible: true, text: td("dinContactHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    din_ct_tx: tc(CONTACT, { isVisible: true, text: td("dinContactText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    din_ct_first: fi(CONTACT, { label: td("firstName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "firstName", changeable: false }),
    din_ct_last: fi(CONTACT, { label: td("lastName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "lastName", changeable: false }),
    din_ct_email: fi(CONTACT, { label: td("email"), overwriteType: "EMAIL", requiredField: true, inputFieldVariable: "email", changeable: false, locked: true }),
    din_ct_phone: fi(CONTACT, { label: td("phoneNumber"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "phone", changeable: false }),
    din_ct_msg: fi(CONTACT, { label: td("messageLabel"), overwriteType: "STRING", requiredField: true, inputFieldVariable: "message", multiline: true, gridArea: "col-span-12", changeable: false, locked: true }),
    din_ct_btn: btn(CONTACT, { linkType: "internal", linkUrl: "", text: td("dinContactButton"), widthMode: "full", buttonType: "submit", displayName: "button", fixedNavigation: false }),
  });
};

// ── Meta sections (AppBar + Footer) ───────────────────────────────────

/** Cream bar, brass rules, logo + serif nav + "Tisch reservieren" CTA. */
export const dinnerAppbar = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "AppBarSection" }, isCanvas: false,
    props: {
      padding: "p-4 wb-md:p-5 wb-lg:p-6", isVisible: true,
      backgroundColor: "#fffcf7",
      border: true, borderColor: "#e8dcbf", borderWidth: "border", borderStyle: "border-solid", borderSides: ["bottom"],
      sectionOrder: ["logo", "links", "social"],
      isLogoVisible: true, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-10", logoPosition: "left",
      isLinksVisible: true, linksPosition: "center",
      links: [
        { label: td("dinLinkMenu"), url: "#din_agenda", linkType: "internal" },
        { label: td("dinLinkBrigade"), url: "#din_speaker", linkType: "internal" },
        { label: td("dinLinkFaq"), url: "#din_faq", linkType: "internal" },
        { label: td("dinLinkContact"), url: "#din_contact", linkType: "internal" },
      ],
      linksColor: INK, linksFontSize: "text-sm", linksFontWeight: "font-medium",
      isSocialVisible: false, socialIcons: [], socialPosition: "right", socialSize: 24,
      logoutButtonSize: "small", logoutButtonText: td("dinHeroPrimaryButton"),
      logoutButtonBorder: true, logoutButtonBackgroundColor: ACCENT, logoutButtonColor: "#fffcf7", logoutButtonBorderColor: ACCENT,
    },
    displayName: "AppBar", custom: { icon: "IconLayoutNavbar" },
    parent: "DCH9WIIT55", hidden: false, nodes: [], linkedNodes: {},
  });

/** Dark espresso footer, brass top rule, four link groups + social + copyright. */
export const dinnerFooter = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "FooterSection" }, isCanvas: false,
    props: {
      padding: "px-4 py-10 wb-md:px-6 wb-lg:px-8", isVisible: true,
      backgroundColor: DARK,
      border: true, borderColor: ACCENT, borderWidth: "border-4", borderStyle: "border-solid", borderSides: ["top"],
      sectionOrder: ["links", "social"],
      isLogoVisible: false, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-auto",
      isLinkGroupsVisible: true,
      linkGroups: [
        { title: td("dinFooterMaison"), links: [
          { label: td("dinLinkMenu"), url: "#din_agenda", linkType: "internal" },
          { label: td("dinLinkBrigade"), url: "#din_speaker", linkType: "internal" },
          { label: td("dinLinkCarnet"), url: "#din_blog", linkType: "internal" },
        ] },
        { title: td("dinFooterEvent"), links: [
          { label: td("dinLinkLocation"), url: "https://www.google.com/maps/search/?api=1&query=Tucholskystrasse+18+10117+Berlin", linkType: "external" },
          { label: td("dinLinkCalendar"), url: "https://calendar.google.com/calendar/render?action=TEMPLATE&text=Soiree+Menu+Degustation", linkType: "external" },
          { label: td("dinLinkReserve"), url: "/register", linkType: "internal" },
        ] },
        { title: td("dinFooterSupport"), links: [
          { label: td("dinLinkContact"), url: "#din_contact", linkType: "internal" },
          { label: td("dinLinkFaq"), url: "#din_faq", linkType: "internal" },
          { label: td("dinLinkAddress"), url: "#din_map", linkType: "internal" },
        ] },
        { title: td("dinFooterLegal"), links: [
          { label: td("dinLinkPrivacy"), url: "/privacy", linkType: "internal" },
          { label: td("dinLinkImprint"), url: "/imprint", linkType: "internal" },
          { label: td("dinLinkTerms"), url: "/terms", linkType: "internal" },
        ] },
      ],
      linksTitleColor: "#fffcf7", linksTitleFontSize: "text-sm", linksTitleFontWeight: "font-semibold",
      linksColor: "#c9a96e", linksFontSize: "text-sm", linksFontWeight: "font-normal", linksHoverColor: "hover:font-primary",
      isSocialVisible: true,
      socialIcons: [
        { altText: "instagram", platform: "instagram", url: "https://instagram.com" },
        { altText: "facebook", platform: "facebook", url: "https://facebook.com" },
      ],
      socialColor: "#c9a96e", socialHoverColor: "hover:font-primary", socialSize: 20, socialTitle: td("followUs"),
      isCopyrightVisible: true, copyrightColor: "#8b6f3a", copyrightFontSize: "text-xs", copyrightCompanyName: td("yourCompany"),
    },
    displayName: "Footer", custom: { icon: "IconMenu2" },
    parent: "xaUW2UDZT8", hidden: false, nodes: [], linkedNodes: {},
  });

// ── Theme CSS (P9 WS4-B3) ─────────────────────────────────────────────
//
// Marker comment first so a later user edit is distinguishable from the
// pristine preset. Composed from the shared premium-css builders (WS3).
// The hero stays a light menu-card sheet (image right, node props); the css
// layers the warm-celebration treatment on top: a candlelight radial glow in
// brass floating over the cream, a fine burgundy rule beneath the headline
// block, uppercase letterspaced kickers, and the self-hosted faces so the
// preset's Playfair Display/Inter stacks actually resolve.
const CUSTOM_CSS = `/* theme:dinner */
${fontFaces([
  { family: "Playfair Display", file: "playfair-display", weightRange: "400 900" },
  { family: "Inter", file: "inter-regular", weightRange: "400" },
  { family: "Inter", file: "inter-bold", weightRange: "700" },
])}

/* Menu-card hero — clip + isolate the candlelight ornament layers */
#din_hero {
${heroShell}
}

/* Candlelight motif: a soft brass glow washing in from the top-left corner,
   where the menu headline sits (kept faint — the sheet stays cream) */
#din_hero::before {
${decorLayer}
  width: 440px;
  height: 440px;
  top: -120px;
  left: -100px;
  background: radial-gradient(circle at 50% 50%, rgba(201, 169, 110, 0.22) 0%, rgba(201, 169, 110, 0.08) 52%, rgba(201, 169, 110, 0) 74%);
}

/* Fine burgundy rule beneath the headline block (menu-card divider) */
#din_hero::after {
${decorLayer}
  left: 6%;
  bottom: 12%;
  width: 110px;
  height: 1px;
  background: ${ACCENT};
}

/* Uppercase letterspaced kickers (the text-xs eyebrows in each section) */
#din_hero .text-xs, #din_numbers .text-xs, #din_agenda .text-xs, #din_speaker .text-xs,
#din_text .text-xs, #din_image .text-xs, #din_countdown .text-xs, #din_gallery .text-xs,
#din_video .text-xs, #din_testimonials .text-xs, #din_blog .text-xs, #din_faq .text-xs,
#din_map .text-xs, #din_contact .text-xs {
  text-transform: uppercase;
  letter-spacing: 0.22em;
}

${motionGate(`${fadeUpKeyframes("din", 18)}
  @keyframes dinGlowIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes dinGlowBreathe {
    0%, 100% { transform: scale(1); opacity: 0.85; }
    50% { transform: scale(1.06); opacity: 1; }
  }

  /* Celebration entrance: the sheet settles, the candlelight fades in softly */
  #din_hero { animation: dinFadeUp 0.8s ${EASE} both; }
  #din_hero::before {
    animation: dinGlowIn 1.6s ${EASE} 0.4s both, dinGlowBreathe 12s ease-in-out 2s infinite;
  }

${revealBlock("din", ["numbers", "datainput", "agenda", "speaker", "text", "image", "countdown", "gallery", "video", "testimonials", "blog", "faq", "map", "contact"], 30)}`)}
`;

// ── Style preset (editorial-menu) ─────────────────────────────────────

export const dinnerPreset: WebsiteThemeStylePreset = {
  heroImage: `${W}/hero-einladung.jpg`,
  logoSrc: LOGO,
  colorPalette: { primary: ACCENT, secondary: "#c9a96e", background: "#fffcf7" },
  fonts: { header: SERIF, body: "Inter, 'Helvetica Neue', Arial, sans-serif" },
  rounding: "rounded-lg",
  recommendedSize: "medium",
  bodyTheme: { fontColor: MUTE, fontSize: "16px", layoutOption: "full-width" },
  headingsTheme: {
    h1: { fontFamily: SERIF, fontSize: "48px", fontColor: INK },
    h2: { fontFamily: SERIF, fontSize: "28px", fontColor: INK },
  },
  buttonsTheme: {
    primary: { bgColor: ACCENT, bgHoverColor: "#5e161f", borderColor: ACCENT, borderHoverColor: "#5e161f", fontColor: "#fffcf7", fontHoverColor: "#fffcf7", fontSize: "16px", fontHoverSize: "16px", borderRadius: "rounded-lg", hoverBorderRadius: "rounded-lg" },
    secondary: { bgColor: "transparent", bgHoverColor: "#faf5ea", borderColor: "#c9a96e", borderHoverColor: "#8b6f3a", fontColor: "#8b6f3a", fontHoverColor: ACCENT, fontSize: "16px", fontHoverSize: "16px", borderRadius: "rounded-lg", hoverBorderRadius: "rounded-lg" },
  },
  hyperlinkTheme: { fontColor: ACCENT, fontHoverColor: "#5e161f", underline: false },
  formInputTheme: { labelFontColor: INK, inputFontColor: INK, backgroundColor: "#fffcf7", border: true, borderColor: "#e8dcbf", borderRadius: "rounded-lg", borderStyle: "border-solid", borderWidth: "border", borderSides: [], fontFamily: "Inter, 'Helvetica Neue', Arial, sans-serif", fontSize: "text-sm", errorColor: "#DC2626" },
  customCss: CUSTOM_CSS,
};
